import {
  CogIcon,
  UsersIcon,
  CurrencyPoundIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { systemSettings, membershipTypes, yearlyPrices } from '../../utils/stubData';

function Settings() {
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', name: 'General', icon: CogIcon },
    { id: 'membership', name: 'Membership Types', icon: UsersIcon },
    { id: 'pricing', name: 'Pricing', icon: CurrencyPoundIcon },
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'renewals', name: 'Renewals', icon: ArrowPathIcon },
    { id: 'integrations', name: 'Integrations', icon: PuzzlePieceIcon },
  ];

  const GeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="settings-club-name" className="block text-sm text-hdlcc-textMuted mb-2">
            Club Name
          </label>
          <input
            id="settings-club-name"
            type="text"
            defaultValue={systemSettings.clubName}
            className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="settings-club-email" className="block text-sm text-hdlcc-textMuted mb-2">
            Club Email
          </label>
          <input
            id="settings-club-email"
            type="email"
            defaultValue={systemSettings.clubEmail}
            className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="settings-club-phone" className="block text-sm text-hdlcc-textMuted mb-2">
            Club Phone
          </label>
          <input
            id="settings-club-phone"
            type="tel"
            defaultValue={systemSettings.clubPhone}
            className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Club Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Address Line 1"
            defaultValue={systemSettings.clubAddress.line1}
            className="p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
          <input
            type="text"
            placeholder="Address Line 2"
            defaultValue={systemSettings.clubAddress.line2}
            className="p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
          <input
            type="text"
            placeholder="Town/City"
            defaultValue={systemSettings.clubAddress.town}
            className="p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
          <input
            type="text"
            placeholder="County"
            defaultValue={systemSettings.clubAddress.county}
            className="p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
          <input
            type="text"
            placeholder="Postcode"
            defaultValue={systemSettings.clubAddress.postcode}
            className="p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button className="bg-hdlcc-accentRed text-white px-6 py-2 rounded hover:bg-red-600 transition-colors">
          Save General Settings
        </button>
      </div>
    </div>
  );

  const MembershipSettings = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-hdlcc-textDark">Membership Types</h3>
        <button className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
          Add New Type
        </button>
      </div>

      <div className="space-y-4">
        {membershipTypes.map(type => (
          <div key={type.id} className="bg-gray-50 p-4 rounded border border-gray-300">
            <div className="flex justify-between items-start">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor={`membership-type-name-${type.id}`}
                    className="block text-sm text-hdlcc-textMuted mb-1"
                  >
                    Type Name
                  </label>
                  <input
                    id={`membership-type-name-${type.id}`}
                    type="text"
                    defaultValue={type.name}
                    className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`membership-type-multiple-${type.id}`}
                    className="block text-sm text-hdlcc-textMuted mb-1"
                  >
                    Multiple Members
                  </label>
                  <select
                    id={`membership-type-multiple-${type.id}`}
                    defaultValue={type.allowsMultipleMembers.toString()}
                    className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                {type.allowsMultipleMembers && (
                  <>
                    <div>
                      <label
                        htmlFor={`membership-type-min-${type.id}`}
                        className="block text-sm text-hdlcc-textMuted mb-1"
                      >
                        Min Members
                      </label>
                      <input
                        id={`membership-type-min-${type.id}`}
                        type="number"
                        defaultValue={type.minMembers}
                        className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`membership-type-max-${type.id}`}
                        className="block text-sm text-hdlcc-textMuted mb-1"
                      >
                        Max Members
                      </label>
                      <input
                        id={`membership-type-max-${type.id}`}
                        type="number"
                        defaultValue={type.maxMembers}
                        className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                      />
                    </div>
                  </>
                )}
              </div>

              <button className="ml-4 text-hdlcc-accentRed hover:text-red-600">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const PricingSettings = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-hdlcc-textDark">Yearly Pricing</h3>
        <button className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
          Add New Price
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <thead>
            <tr className="border-b border-gray-300 bg-gray-100">
              <th className="px-4 py-3 text-left text-hdlcc-textDark">Membership Type</th>
              <th className="px-4 py-3 text-left text-hdlcc-textDark">Year</th>
              <th className="px-4 py-3 text-left text-hdlcc-textDark">Base Price</th>
              <th className="px-4 py-3 text-left text-hdlcc-textDark">Effective From</th>
              <th className="px-4 py-3 text-left text-hdlcc-textDark">Effective To</th>
              <th className="px-4 py-3 text-left text-hdlcc-textDark">Actions</th>
            </tr>
          </thead>
          <tbody>
            {yearlyPrices
              .filter(p => p.year >= 2024)
              .map(price => {
                const type = membershipTypes.find(t => t.id === price.membershipTypeId);
                return (
                  <tr key={price.id} className="border-b border-gray-200/50">
                    <td className="px-4 py-3 text-hdlcc-textDark">{type?.name}</td>
                    <td className="px-4 py-3 text-hdlcc-textDark">{price.year}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={price.basePrice}
                        className="w-24 p-1 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-hdlcc-textMuted">
                      {price.effectiveFrom}
                    </td>
                    <td className="px-4 py-3 text-sm text-hdlcc-textMuted">{price.effectiveTo}</td>
                    <td className="px-4 py-3">
                      <button className="text-hdlcc-accentRed hover:text-red-600 text-sm">
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-50 p-4 rounded border border-gray-300">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            defaultChecked={systemSettings.proRataEnabled}
            className="rounded"
          />
          <span className="text-hdlcc-textDark">Enable Pro-rata Pricing for New Memberships</span>
        </label>
        <p className="text-sm text-hdlcc-textMuted mt-2">
          When enabled, new members joining mid-year will be charged a prorated fee based on the
          remaining months.
        </p>
      </div>
    </div>
  );

  const EmailSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="settings-email-from" className="block text-sm text-hdlcc-textMuted mb-2">
            From Email Address
          </label>
          <input
            id="settings-email-from"
            type="email"
            defaultValue={systemSettings.emailFrom}
            className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="settings-email-reply-to"
            className="block text-sm text-hdlcc-textMuted mb-2"
          >
            Reply-To Email Address
          </label>
          <input
            id="settings-email-reply-to"
            type="email"
            defaultValue={systemSettings.emailReplyTo}
            className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="settings-sendgrid-api-key"
          className="block text-sm text-hdlcc-textMuted mb-2"
        >
          SendGrid API Key
        </label>
        <input
          id="settings-sendgrid-api-key"
          type="password"
          defaultValue={systemSettings.sendgridApiKey}
          className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
        />
      </div>

      <div className="flex justify-end">
        <button className="bg-hdlcc-accentRed text-white px-6 py-2 rounded hover:bg-red-600 transition-colors">
          Save Email Settings
        </button>
      </div>
    </div>
  );

  const RenewalSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Renewal Reminder Schedule</h3>
        <p className="text-sm text-hdlcc-textMuted mb-4">
          Configure when automated renewal reminder emails are sent before membership expiry
        </p>

        <div className="space-y-3">
          {systemSettings.renewalReminderDays.map((days, index) => (
            <div key={index} className="flex items-center space-x-4">
              <input
                type="number"
                defaultValue={days}
                className="w-24 p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
              />
              <span className="text-sm text-hdlcc-textDark">days before expiry</span>
              <button className="text-hdlcc-accentRed hover:text-red-600 text-sm">Remove</button>
            </div>
          ))}
        </div>

        <button className="mt-4 text-hdlcc-accentRed hover:text-red-600 text-sm">
          + Add Another Reminder
        </button>
      </div>

      <div>
        <label htmlFor="settings-grace-period" className="block text-sm text-hdlcc-textMuted mb-2">
          Grace Period (Days)
        </label>
        <input
          id="settings-grace-period"
          type="number"
          defaultValue={systemSettings.gracePeriodDays}
          className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
        />
        <p className="text-xs text-hdlcc-textMuted mt-1">
          Number of days after expiry before membership is deactivated
        </p>
      </div>

      <div className="bg-gray-50 p-4 rounded border border-gray-300">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            defaultChecked={!systemSettings.autoApprovalEnabled}
            className="rounded"
          />
          <span className="text-hdlcc-textDark">Require Manual Approval for Renewals</span>
        </label>
      </div>

      <div className="flex justify-end">
        <button className="bg-hdlcc-accentRed text-white px-6 py-2 rounded hover:bg-red-600 transition-colors">
          Save Renewal Settings
        </button>
      </div>
    </div>
  );

  const IntegrationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Payment Integration</h3>
        <div>
          <label htmlFor="settings-stripe-key" className="block text-sm text-hdlcc-textMuted mb-2">
            Stripe Publishable Key
          </label>
          <input
            id="settings-stripe-key"
            type="text"
            defaultValue={systemSettings.stripePublishableKey}
            className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 p-4 rounded border border-gray-300">
          <h4 className="font-bold mb-2 text-hdlcc-textDark">Stripe</h4>
          <p className="text-sm text-hdlcc-textMuted mb-3">Online card payments</p>
          <span className="px-3 py-1 bg-green-900 text-green-200 rounded text-xs">Connected</span>
        </div>

        <div className="bg-gray-50 p-4 rounded border border-gray-300">
          <h4 className="font-bold mb-2 text-hdlcc-textDark">SendGrid</h4>
          <p className="text-sm text-hdlcc-textMuted mb-3">Email delivery service</p>
          <span className="px-3 py-1 bg-green-900 text-green-200 rounded text-xs">Connected</span>
        </div>
      </div>

      <div className="flex justify-end">
        <button className="bg-hdlcc-accentRed text-white px-6 py-2 rounded hover:bg-red-600 transition-colors">
          Save Integration Settings
        </button>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'membership':
        return <MembershipSettings />;
      case 'pricing':
        return <PricingSettings />;
      case 'email':
        return <EmailSettings />;
      case 'renewals':
        return <RenewalSettings />;
      case 'integrations':
        return <IntegrationSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase">System Settings</h2>
        <p className="text-hdlcc-textMuted">Configure system preferences and integrations</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 overflow-x-auto">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-hdlcc-accentRed text-white'
                  : 'bg-hdlcc-bgWhite text-hdlcc-textDark hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <IconComponent className="w-5 h-5" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-hdlcc-bgWhite p-6 rounded shadow-card border border-gray-200">
        {renderTabContent()}
      </div>
    </div>
  );
}

export default Settings;
